import BaseListPresenter from "../../base/BaseListPresenter";
import { getCurrentUserUseCase } from "../../usecases/user";
import {
  format,
  startOfDay,
  addHours,
  addMinutes,
  addDays,
  parseISO,
} from "date-fns";
import xhr from "../../usecases/xhr/Xhr";

class GrossSalesPresenter extends BaseListPresenter {
  init() {
    this.store = [];
    // this.limit = 20;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.filterDate = {};
    this.filterStore = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  componentDidMount() {
    this.init();
    this.findStore();
    return this.getObjects();
  }

  componentWillUnmount() {
    this.cleanup();
  }

  async getObjects() {
    this.reset();
    this.find();
  }

  async findStore() {
    const user = await getCurrentUserUseCase().execute();
    const storeQuery = {
      include: ["storeAnalytic", "storeAnalytic.expectedRelation"],
      where: { email: user.email },
    };
    this.findObjectUseCase.abort();
    this.store = await this.findObjectUseCase.execute("users", storeQuery);

    console.log("this.store", this.store);

    const store = (this.store?.[0]?.storeAnalytic ?? []).sort((a, b) =>
      a.name.toUpperCase().localeCompare(b.name.toUpperCase()),
    );
    console.log("this.store22", this.store);
    this.view.setStore(store);
  }

  async find() {
    try {
      if (this.abortController && !this.abortController.signal.aborted) {
        this.abortController.abort();
        console.log("Aborted previous requests");
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      this.abortController = new AbortController();
      this.objects = [];
      const startTime = Date.now();

      const query = this.createQuery({ skip: 0, limit: this.limit });
      delete query.limit;
      delete query.include;

      const startDate = query.where?.tran_date?.$gte || "";
      const endDate = query.where?.tran_date?.$lte || "";

      const dateRanges = this.generateDateRanges(startDate, endDate);

      for (let i = 0; i < dateRanges.length; i++) {
        const range = dateRanges[i];

        const chunkQuery = {
          ...query,
          where: {
            ...query.where,
            tran_date: {
              $gte: range.start,
              $lte: range.end,
            },
          },
        };

        try {
          const chunkData = await xhr.get("/transactions", {
            params: chunkQuery,
            signal: this.abortController.signal,
          });

          this.objects = this.objects.concat(chunkData);
          this.view.setObjects(this.objects);
        } catch (chunkError) {
          if (chunkError.name === "AbortError") {
            console.log("Request was aborted at chunk", i + 1);
            return;
          }
          console.error(`Error fetching ${range.label}:`, chunkError.message);
        }
      }

      const downloadTime = ((Date.now() - startTime) / 1000).toFixed(1);
      console.log(
        `✅ Complete! ${this.objects.length.toLocaleString()} records in ${downloadTime}s`,
      );
      this.abortController = null;
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Entire operation was aborted");
        return;
      }
      console.error("Error:", error.message);
    }
  }

  cleanup() {
    if (this.abortController) {
      console.log("🧹 Cleaning up...");
      this.abortController.abort();
      this.abortController = null;
    }
    this.objects = [];
  }

  // Simple range generator: ≤31 days = 1 request, >31 days = monthly chunks
  generateDateRanges(startDateStr, endDateStr) {
    const start = new Date(startDateStr.split(" ")[0]);
    const end = new Date(endDateStr.split(" ")[0]);

    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

    console.log(`📆 Date range: ${daysDiff} days`);

    // If 31 days or less, single request
    if (daysDiff <= 31) {
      console.log("Strategy: Single request (≤31 days)");
      return [
        {
          start: this.formatDate(start),
          end: this.formatDate(end),
          label: `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
        },
      ];
    }

    // More than 31 days, use monthly chunks
    console.log("Strategy: Monthly chunks (>31 days)");
    return this.generateMonthlyRanges(start, end);
  }

  // Generate monthly ranges
  generateMonthlyRanges(startDate, endDate) {
    const ranges = [];
    let current = new Date(startDate);

    while (current < endDate) {
      const monthStart = new Date(current);
      const monthEnd = new Date(
        current.getFullYear(),
        current.getMonth() + 1,
        0,
        23,
        59,
        59,
      );

      if (monthEnd > endDate) {
        monthEnd.setTime(endDate.getTime());
      }

      ranges.push({
        start: this.formatDate(monthStart),
        end: this.formatDate(monthEnd),
        label: monthStart.toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
      });

      current.setMonth(current.getMonth() + 1);
    }

    return ranges;
  }

  // Format date for API
  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day} 05:00:00.000`;
  }

  createQuery({ skip = 0, limit = this.limit } = {}) {
    const storeCodes =
      this.store?.flatMap((s) => s.storeAnalytic?.map((a) => a?.name)) ?? [];

    const escapedSearch = this.searchAny
      ?.trim()
      ?.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const whereClause = {
      ...(this.where || {}),
      ...(this.filter || {}),
      ...(this.filterDate || {}),
    };

    if (this.filterStore === "AllStore") {
      if (storeCodes.length > 0) {
        whereClause.store_code = { $in: storeCodes };
      }
    } else if (this.filterStore) {
      whereClause.store_code = this.filterStore;
    }

    // 3) Build final query
    const query = {
      limit,
      skip,
      where: whereClause,
      include: this.include,
      keys: this.keys ?? [
        "store_code",
        "status",
        "tran_date",
        "total",
        "total_tax",
        "createdAt",
        "discount_amount",
        "tran_id",
      ],
      sort: this.sort,
    };

    return query;
  }

  // async findObjects() {
  //   try {
  //     const user = await getCurrentUserUseCase().execute();
  //     const storeQuery = {
  //       include: ["all"],
  //       where: { email: user.email },
  //     };
  //     this.store = await this.findObjectUseCase.execute("users", storeQuery);
  //     const store = this.store[0].storeAnalytic.sort((a, b) => {
  //       const nameA = a.name.toUpperCase(); // ignore upper and lowercase
  //       const nameB = b.name.toUpperCase(); // ignore upper and lowercase
  //       if (nameA < nameB) {
  //         return -1;
  //       }
  //       if (nameA > nameB) {
  //         return 1;
  //       }
  //       return 0; // names must be equal
  //     });
  //     this.view.setStore(store);
  //   } catch (error) {
  //     console.error("Error fetching user or store data:", error);
  //     return;
  //   }

  //   const skip = (this.current - 1) * this.limit;
  //   const storeCodes = this.store?.flatMap((storeItem) =>
  //     storeItem.storeAnalytic?.map((analytic) => analytic?.name),
  //   );

  //   const query = {
  //     // limit: 2000000,
  //     skip: skip,
  //     where: {
  //       store_code:
  //         this.filterStore === "AllStore"
  //           ? { $in: storeCodes }
  //           : this.filterStore,
  //       tran_date: this.filterDate && this.filterDate,
  //     },
  //     include: ["all"],
  //     sort: this.sort,
  //     keys: [
  //       "store_code",
  //       "status",
  //       "tran_date",
  //       "total",
  //       "total_tax",
  //       "createdAt",
  //       "discount_amount",
  //     ],
  //   };

  //   try {
  //     this.showProgress();
  //     this.findObjectUseCase.abort();
  //     const { count } = await this.countObjectUseCase.execute(
  //       this.view.getCollectionName(),
  //       { where: query.where },
  //     );

  //     this.count = count;
  //     console.log("this.count", this.count);
  //     this.view.setCount(this.count);

  //     let timeoutId = null;

  //     // Handle delayed "not found"
  //     if (this.count === 0) {
  //       timeoutId = setTimeout(() => {
  //         if (this.count === 0) {
  //           this.view.setNotFound(true);
  //           this.view.setObjects([]);
  //           this.view.setTotal(0);
  //         }
  //       }, 30000); // 15 seconds delay
  //     }

  //     // Batching logic
  //     let buffer = [];

  //     const flushBuffer = () => {
  //       if (buffer.length > 0) {
  //         this.view.setState((prevState) => ({
  //           objects: [...prevState.objects, ...buffer],
  //         }));
  //         this.objects = this.objects.concat(buffer);
  //         this.view.setTotal(this.objects.length);
  //         this.view.setObjects(this.objects);
  //         buffer = [];
  //       }
  //     };

  //     const onData = (objects) => {
  //       if (objects.length > 0 && timeoutId) {
  //         clearTimeout(timeoutId);
  //         timeoutId = null;
  //         this.view.setNotFound(false);
  //       }
  //       buffer.push(...objects);
  //     };

  //     const flushInterval = setInterval(flushBuffer, 2000);

  //     await this.findObjectUseCase.execute(
  //       this.view.getCollectionName(),
  //       query,
  //       {
  //         timeout: 100000,
  //         onData,
  //       },
  //     );

  //     clearInterval(flushInterval);
  //     flushBuffer();
  //     // const objects = await this.findObjectUseCase.execute(
  //     //   "transactions",
  //     //   query
  //     // );
  //     // this.objects = this.objects.concat(objects);
  //     // this.view.setTotal(this.objects.length);
  //     // this.view.setObjects(this.objects);
  //   } catch (error) {
  //     console.error("Error fetching objects:", error);
  //     this.view.showError(error);
  //   } finally {
  //     this.hideProgress();
  //     this.progress = false;
  //   }
  // }

  onChangeStore(where) {
    this.filterStore = where;
    // this.getObjects();
  }

  onChangeDate(where) {
    if (where !== "AllMonths" && where) {
      const { $gte, $lte } = where;

      if ($gte && $lte) {
        const startDate = addHours(startOfDay(parseISO($gte)), 5);
        const endDate = addMinutes(
          addHours(addDays(startOfDay(parseISO($lte)), 0), 4),
          59,
        );

        const formattedStartDate = format(startDate, "yyyy-MM-dd HH:mm:ss.SSS");
        const formattedEndDate = format(endDate, "yyyy-MM-dd HH:mm:ss.SSS");

        this.filterDate = {
          tran_date: {
            $gte: formattedStartDate,
            $lte: formattedEndDate,
          },
        };
      }
    } else {
      delete this.filterDate;
    }
    this.getObjects();
  }
}

export default GrossSalesPresenter;
